"use client";

import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Phone, Voicemail, Check, Loader2, Mic,
  AlertTriangle, PhoneForwarded, FileText, Plus,
  Trash2, Star, Upload, Pencil, X, Copy, Volume2,
  Play, Square, CheckCircle, AlertCircle, Clock, Sparkles, Zap, Shield, Building2
} from "lucide-react";
import { cn } from "@/app/_module/lib/utils";
import { Card, CardContent } from "@/app/_module/components/ui/card";
import { Label } from "@/app/_module/components/ui/label";
import { Switch } from "@/app/_module/components/ui/switch";
import { Input } from "@/app/_module/components/ui/input";
import { useToast } from "@/app/_module/hooks/use-toast";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { Textarea } from "@/app/_module/components/ui/textarea";
import { Badge } from "@/app/_module/components/ui/badge";
import { Button } from "@/app/_module/components/ui/button";
import { VoicemailDropLibrary } from "../VoicemailDropLibrary";
import { useCallScripts, useCreateScript, useUpdateScript, useDeleteScript, useDuplicateTemplate } from "@/app/_module/hooks/useCallScripts";
import type { CallScript } from "@/app/_module/services/scriptService";
import { SCRIPT_CATEGORIES, PRODUCT_TYPES } from "@/app/_module/constants/scriptCategories";
import {
  useVoiceClones,
  useCreateVoiceClone,
  useDeleteVoiceClone,
  useSetDefaultVoiceClone,
  usePreviewVoiceClone,
  useTestRVM,
} from "@/app/_module/hooks/useVoiceClone";
import type { VoiceClone } from "@/app/_module/services/voiceCloneService";
import InsuraBotIcon from "@/app/(authenticated)/ai-calling/_modules/components/InsuraBotIcon";
import { showToast } from "@/app/_module/utils/show-toast";
import { DialIQSettings } from "@/app/(authenticated)/settings/_modules/components/account/DialIQSettings";
import { DialerSystemCard } from "./DialerSystemCard";
import { AudioCheckCard } from "./AudioCheckCard";
import type { DialIQUserSettings } from "@/app/_module/services/redialService.types";
import { DIAL_IQ_ENABLED } from "@/app/_module/utils/featureFlags";

interface DialerPreferences {
  autoVoicemailEnabled: boolean;
  voicemailPrompt: string;
  callTimeout: number;
  selectedVoicemailDropId?: string;
  leadPoolingEnabled: boolean;
  callRecordingEnabled: boolean;
  dialAttempts: number; // 1 = no retry, 2 = one retry, 3 = two retries
  voicemailDetectionMode: 'fast' | 'strict';
  powerDialerVmSpeed: 'fast' | 'balanced' | 'safe';
  voicemailDropRules: Record<string, string>;
  voicemailDropTagRules: Record<string, string>;
  voicemailDropStageRules: Record<string, string>;
}

interface VoicemailDrop {
  id: string;
  name: string;
  description?: string;
  isDefault: boolean;
}

// ── Animation variants ────────────────────────────────────────
const EASE = [0.22, 1, 0.36, 1] as [number, number, number, number];

const stagger = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: { staggerChildren: 0.07, delayChildren: 0.05 },
  },
};

const fadeUp = {
  hidden: { opacity: 0, y: 14 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: EASE },
  },
};

const expand = {
  initial: { height: 0, opacity: 0 },
  animate: {
    height: "auto" as const,
    opacity: 1,
    transition: { duration: 0.35, ease: EASE },
  },
  exit: {
    height: 0,
    opacity: 0,
    transition: { duration: 0.25, ease: EASE },
  },
};

// ── Main Component ────────────────────────────────────────────
export function DialerSettings() {
  const { toast } = useToast();
  const { user, refetchUser } = useUserContext();
  const isBasicPlan = ['basic', 'trial'].includes((user?.user as any)?.accountType || 'basic');
  const [preferences, setPreferences] = useState<DialerPreferences>({
    autoVoicemailEnabled: false,
    voicemailPrompt:
      "Hi, this is [Your Name] calling about your insurance inquiry. I'd love to connect with you. Please give me a call back at your earliest convenience. Thank you!",
    callTimeout: 30,
    selectedVoicemailDropId: undefined,
    leadPoolingEnabled: false,
    callRecordingEnabled: true,
    dialAttempts: 2,
    voicemailDetectionMode: 'fast',
    powerDialerVmSpeed: 'balanced',
    voicemailDropRules: {},
    voicemailDropTagRules: {},
    voicemailDropStageRules: {},
  });
  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [voicemailDrops, setVoicemailDrops] = useState<VoicemailDrop[]>([]);
  const [isLoadingDrops, setIsLoadingDrops] = useState(false);

  const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pendingPreferencesRef = useRef<DialerPreferences | null>(null);
  const isInitialLoadRef = useRef(true);

  const hasDefaultVoicemailDrop = useMemo(() => {
    return voicemailDrops.some((drop) => drop.isDefault);
  }, [voicemailDrops]);

  const showVoicemailWarning =
    preferences.autoVoicemailEnabled && !hasDefaultVoicemailDrop && !isLoadingDrops;

  // ── Auto-save ──
  const autoSave = useCallback(
    async (prefsToSave: DialerPreferences) => {
      try {
        setSaveStatus("saving");
        await api.put({
          url: getApiUrl("/dialer-preferences"),
          body: prefsToSave,
          auth: true,
        });
        setSaveStatus("saved");
        setTimeout(() => setSaveStatus("idle"), 2000);
      } catch {
        setSaveStatus("error");
        toast({
          title: "Save Failed",
          description: "Failed to save dialer preferences. Please try again.",
          variant: "destructive",
        });
        setTimeout(() => setSaveStatus("idle"), 3000);
      }
    },
    [toast]
  );

  useEffect(() => {
    if (isInitialLoadRef.current) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    pendingPreferencesRef.current = preferences;
    saveTimeoutRef.current = setTimeout(() => {
      if (pendingPreferencesRef.current) autoSave(pendingPreferencesRef.current);
    }, 500);
    return () => {
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    };
  }, [preferences, autoSave]);

  useEffect(() => {
    fetchDialerPreferences();
    fetchVoicemailDrops();
  }, []);

  const fetchDialerPreferences = async () => {
    try {
      setIsLoading(true);
      const response = await api.get({ url: getApiUrl("/dialer-preferences"), auth: true });
      const responseBody = response.data as any;
      if (responseBody?.data) {
        const data = responseBody.data;
        setPreferences({
          autoVoicemailEnabled: data.autoVoicemailEnabled ?? false,
          voicemailPrompt: data.voicemailPrompt || preferences.voicemailPrompt,
          callTimeout: data.callTimeout ?? 30,
          selectedVoicemailDropId: data.selectedVoicemailDropId,
          leadPoolingEnabled: data.leadPoolingEnabled ?? false,
          callRecordingEnabled: data.callRecordingEnabled ?? true,
          dialAttempts: data.dialAttempts ?? (data.doubleDialEnabled ? 2 : 1), // Migration fallback
          voicemailDetectionMode: data.voicemailDetectionMode === 'strict' ? 'strict' : 'fast',
          powerDialerVmSpeed: ['fast', 'balanced', 'safe'].includes(data.powerDialerVmSpeed) ? data.powerDialerVmSpeed : 'balanced',
          voicemailDropRules: data.voicemailDropRules || {},
          voicemailDropTagRules: data.voicemailDropTagRules || {},
          voicemailDropStageRules: data.voicemailDropStageRules || {},
        });
      }
    } catch {
      // Use defaults
    } finally {
      setIsLoading(false);
      setTimeout(() => {
        isInitialLoadRef.current = false;
      }, 100);
    }
  };

  const fetchVoicemailDrops = async () => {
    try {
      setIsLoadingDrops(true);
      const response = await api.get({
        url: getApiUrl("/communication/voicemail-drops"),
        auth: true,
      });
      if (response.data) setVoicemailDrops(response.data as VoicemailDrop[]);
    } catch {
      // Silently fail
    } finally {
      setIsLoadingDrops(false);
    }
  };

  const handleToggleChange = (field: keyof DialerPreferences, value: boolean) => {
    setPreferences((prev) => ({ ...prev, [field]: value }));
  };


  // ── Loading skeleton ──
  if (isLoading) {
    return (
      <Card className="overflow-hidden">
        <div className="p-6 space-y-5">
          <div className="flex items-center gap-4">
            <div className="h-11 w-11 rounded-xl bg-muted animate-pulse" />
            <div className="space-y-2">
              <div className="h-5 w-40 bg-muted rounded animate-pulse" />
              <div className="h-3.5 w-56 bg-muted rounded animate-pulse" />
            </div>
          </div>
          <div className="h-px bg-border" />
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-[72px] rounded-xl bg-muted/50 animate-pulse" />
          ))}
        </div>
      </Card>
    );
  }

  // DialIQ — persist settings into user.app_preferences.dialIq via the
  // dedicated shallow-merge endpoint. Re-throws so the autosave indicator
  // in DialIQSettings can show the failure; no toast spam on every change.
  // Also updates local user context so other pages (e.g. Leads header button
  // visibility) see the change instantly without waiting for a refetch.
  const handleDialIqSave = async (next: DialIQUserSettings) => {
    await api.patch({
      url: getApiUrl(`/users/profile/app-preferences`),
      body: { dialIq: next },
    });
    refetchUser((prev) => ({
      ...prev,
      user: {
        ...prev.user,
        appPreferences: {
          ...((prev.user as any)?.appPreferences || {}),
          dialIq: next,
        },
      } as any,
    }));
  };

  return (
    <>
    <DialerSystemCard />
    <AudioCheckCard />
    <Card className="overflow-hidden">
      {/* ── Header ── */}
      <motion.div
        initial={{ opacity: 0, y: -8 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: EASE }}
        className="px-6 pt-6 pb-4"
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center h-11 w-11 rounded-xl bg-primary text-primary-foreground shadow-sm">
              <Phone className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-xl font-semibold tracking-tight">Dialer Preferences</h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                Fine-tune how your dialer behaves during calls
              </p>
            </div>
          </div>

          {/* Save status pill */}
          <AnimatePresence mode="wait">
            {saveStatus === "saving" && (
              <motion.div
                key="saving"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted text-xs text-muted-foreground"
              >
                <Loader2 className="h-3 w-3 animate-spin" />
                Saving
              </motion.div>
            )}
            {saveStatus === "saved" && (
              <motion.div
                key="saved"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-emerald-50 text-xs text-emerald-700 border border-emerald-200"
              >
                <Check className="h-3 w-3" />
                Saved
              </motion.div>
            )}
            {saveStatus === "error" && (
              <motion.div
                key="error"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-destructive/10 text-xs text-destructive border border-destructive/20"
              >
                Failed to save
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Gradient divider */}
        <div className="mt-5 h-px bg-gradient-to-r from-primary/25 via-primary/10 to-transparent" />
      </motion.div>

      {/* ── Settings ── */}
      <CardContent className="pt-0 pb-6">
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="show"
          className="space-y-3"
        >
          {/* ─── Dial Attempts ─── */}
          <motion.div variants={fadeUp}>
            <div
              className={cn(
                "relative rounded-xl border transition-all duration-300",
                "hover:shadow-md hover:shadow-black/[0.03]",
                preferences.dialAttempts > 1
                  ? "border-primary/20 bg-primary/[0.015]"
                  : "border-border"
              )}
            >
              <div
                className={cn(
                  "absolute left-0 top-3 bottom-3 w-[3px] rounded-full transition-all duration-500",
                  preferences.dialAttempts > 1 ? "bg-primary" : "bg-transparent"
                )}
              />

              <div className="flex items-center justify-between p-4 pl-5">
                <div className="flex items-center gap-4">
                  <div
                    className={cn(
                      "flex items-center justify-center h-10 w-10 rounded-lg transition-colors duration-300",
                      preferences.dialAttempts > 1
                        ? "bg-primary/10 text-primary"
                        : "bg-muted text-muted-foreground"
                    )}
                  >
                    <PhoneForwarded className="h-5 w-5" />
                  </div>
                  <div>
                    <Label className="text-[15px] font-semibold leading-none">
                      Dial Attempts
                      <span className="text-xs font-normal text-muted-foreground ml-1.5">(Power dialer only)</span>
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1.5 max-w-md">
                      How many times to call each lead before moving on
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
                  {([
                    { value: 1, label: "No Retry" },
                    { value: 2, label: "1 Retry" },
                    { value: 3, label: "2 Retries" },
                  ] as const).map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        setPreferences((prev) => ({ ...prev, dialAttempts: option.value }));
                      }}
                      className={cn(
                        "px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200",
                        preferences.dialAttempts === option.value
                          ? "bg-background text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <AnimatePresence>
                {preferences.dialAttempts > 1 && (
                  <motion.div {...expand} className="overflow-hidden">
                    <div className="px-5 pb-4 pl-[76px]">
                      <div className="p-3 rounded-lg border-l-2 border-l-primary bg-primary/5">
                        <p className="text-xs text-foreground/70 leading-relaxed">
                          <strong>How it works:</strong> Voicemails and no-answers are automatically
                          retried{preferences.dialAttempts === 3 ? " up to 2 more times" : " once"}.
                          Answered calls and skipped leads are never retried.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* ─── Power Dialer Voicemail Speed ─── */}
          <motion.div variants={fadeUp}>
            <div
              className={cn(
                "relative rounded-xl border transition-all duration-300",
                "hover:shadow-md hover:shadow-black/[0.03]",
                preferences.powerDialerVmSpeed === 'fast'
                  ? "border-amber-200 bg-amber-50/30"
                  : preferences.powerDialerVmSpeed === 'safe'
                    ? "border-blue-200 bg-blue-50/30"
                    : "border-border"
              )}
            >
              <div
                className={cn(
                  "absolute left-0 top-3 bottom-3 w-[3px] rounded-full transition-all duration-500",
                  preferences.powerDialerVmSpeed === 'fast'
                    ? "bg-amber-500"
                    : preferences.powerDialerVmSpeed === 'safe'
                      ? "bg-blue-500"
                      : "bg-transparent"
                )}
              />

              <div className="flex items-center justify-between p-4 pl-5">
                <div className="flex items-center gap-4">
                  <div
                    className={cn(
                      "flex items-center justify-center h-10 w-10 rounded-lg transition-colors duration-300",
                      preferences.powerDialerVmSpeed === 'fast'
                        ? "bg-amber-100 text-amber-600"
                        : preferences.powerDialerVmSpeed === 'safe'
                          ? "bg-blue-100 text-blue-600"
                          : "bg-muted text-muted-foreground"
                    )}
                  >
                    <Zap className="h-5 w-5" />
                  </div>
                  <div>
                    <Label className="text-[15px] font-semibold leading-none">
                      Power Dialer Voicemail Speed
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1.5 max-w-md">
                      How quickly to skip voicemails during power dialer sessions
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
                  {([
                    { value: 'fast' as const, label: "Fast", icon: Zap },
                    { value: 'balanced' as const, label: "Balanced", icon: Phone },
                    { value: 'safe' as const, label: "Safe", icon: Shield },
                  ]).map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        setPreferences((prev) => ({ ...prev, powerDialerVmSpeed: option.value }));
                      }}
                      className={cn(
                        "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200",
                        preferences.powerDialerVmSpeed === option.value
                          ? "bg-background text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      <option.icon className="h-3.5 w-3.5" />
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <AnimatePresence mode="wait">
                <motion.div key={preferences.powerDialerVmSpeed} {...expand} className="overflow-hidden">
                  <div className="px-5 pb-4 pl-[76px]">
                    <div className={cn(
                      "p-3 rounded-lg border-l-2",
                      preferences.powerDialerVmSpeed === 'fast'
                        ? "border-l-amber-400 bg-amber-50/60"
                        : preferences.powerDialerVmSpeed === 'safe'
                          ? "border-l-blue-400 bg-blue-50/60"
                          : "border-l-primary bg-primary/5"
                    )}>
                      <p className="text-xs text-foreground/70 leading-relaxed">
                        {preferences.powerDialerVmSpeed === 'fast' ? (
                          <>
                            <strong>Fast:</strong> Skips voicemails in about 3 seconds. Best for high-volume sessions where speed matters most. Rarely, a slow answerer might get skipped — you can always call them back.
                          </>
                        ) : preferences.powerDialerVmSpeed === 'safe' ? (
                          <>
                            <strong>Safe:</strong> Waits for the voicemail beep before skipping (10-15 seconds). Zero chance of skipping a real person. Best when every lead counts and you don&apos;t mind the extra wait.
                          </>
                        ) : (
                          <>
                            <strong>Balanced:</strong> Skips voicemails in about 5 seconds. Good mix of speed and accuracy — works well for most agents and lead types.
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>

          {/* ─── Voicemail Filter (Multi-Line) ─── */}
          <motion.div variants={fadeUp}>
            <div
              className={cn(
                "relative rounded-xl border transition-all duration-300",
                "hover:shadow-md hover:shadow-black/[0.03]",
                preferences.voicemailDetectionMode === 'strict'
                  ? "border-primary/20 bg-primary/[0.015]"
                  : "border-border"
              )}
            >
              <div
                className={cn(
                  "absolute left-0 top-3 bottom-3 w-[3px] rounded-full transition-all duration-500",
                  preferences.voicemailDetectionMode === 'strict' ? "bg-primary" : "bg-transparent"
                )}
              />

              <div className="flex items-center justify-between p-4 pl-5">
                <div className="flex items-center gap-4">
                  <div
                    className={cn(
                      "flex items-center justify-center h-10 w-10 rounded-lg transition-colors duration-300",
                      preferences.voicemailDetectionMode === 'strict'
                        ? "bg-primary/10 text-primary"
                        : "bg-muted text-muted-foreground"
                    )}
                  >
                    <Phone className="h-5 w-5" />
                  </div>
                  <div>
                    <Label className="text-[15px] font-semibold leading-none">
                      Multi-Line Voicemail Filter
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1.5 max-w-md">
                      Choose how voicemails are handled when using the multi-line dialer
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-1 bg-muted rounded-lg p-1">
                  {([
                    { value: 'fast' as const, label: "Fast", icon: Zap },
                    { value: 'strict' as const, label: "Strict", icon: Shield },
                  ]).map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        setPreferences((prev) => ({ ...prev, voicemailDetectionMode: option.value }));
                      }}
                      className={cn(
                        "flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-all duration-200",
                        preferences.voicemailDetectionMode === option.value
                          ? "bg-background text-foreground shadow-sm"
                          : "text-muted-foreground hover:text-foreground"
                      )}
                    >
                      <option.icon className="h-3.5 w-3.5" />
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              <AnimatePresence mode="wait">
                <motion.div key={preferences.voicemailDetectionMode} {...expand} className="overflow-hidden">
                  <div className="px-5 pb-4 pl-[76px]">
                    <div className={cn(
                      "p-3 rounded-lg border-l-2",
                      preferences.voicemailDetectionMode === 'strict'
                        ? "border-l-blue-400 bg-blue-50/60"
                        : "border-l-red-400 bg-red-50/60"
                    )}>
                      <p className="text-xs text-foreground/70 leading-relaxed">
                        {preferences.voicemailDetectionMode === 'fast' ? (
                          <>
                            <strong>Fast:</strong> You get connected quicker but may hear a voicemail once in a while. Google Voice screening, Apple call screening, and similar services may still come through — those aren't traditional voicemails.
                          </>
                        ) : (
                          <>
                            <strong>Strict:</strong> Takes a few extra seconds per call but almost never connects you to a voicemail. Note: Google Voice screening, Apple call screening, and similar services may still come through as they behave like a real person answering.
                          </>
                        )}
                      </p>
                    </div>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </motion.div>

          {/* ─── Auto Voicemail Drop (Full-Fledged+ only) ─── */}
          <motion.div variants={fadeUp}>
            <div
              className={cn(
                "relative rounded-xl border transition-all duration-300",
                isBasicPlan
                  ? "opacity-50 border-border bg-muted/30"
                  : "hover:shadow-md hover:shadow-black/[0.03]",
                !isBasicPlan && preferences.autoVoicemailEnabled
                  ? "border-primary/20 bg-primary/[0.015]"
                  : !isBasicPlan ? "border-border" : ""
              )}
            >
              {/* Active accent bar */}
              <div
                className={cn(
                  "absolute left-0 top-3 bottom-3 w-[3px] rounded-full transition-colors duration-300",
                  preferences.autoVoicemailEnabled ? "bg-primary" : "bg-transparent"
                )}
              />

              <div className="flex items-center justify-between p-4 pl-5">
                <div className="flex items-center gap-4">
                  <div
                    className={cn(
                      "flex items-center justify-center h-10 w-10 rounded-lg transition-colors duration-300",
                      preferences.autoVoicemailEnabled
                        ? "bg-primary/10 text-primary"
                        : "bg-muted text-muted-foreground"
                    )}
                  >
                    <Voicemail className="h-5 w-5" />
                  </div>
                  <div>
                    <Label
                      htmlFor="auto-voicemail"
                      className="text-[15px] font-semibold leading-none cursor-pointer"
                    >
                      Auto AI Voice Drop
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1.5 max-w-md">
                      {isBasicPlan ? (
                        <span>
                          Automatically leave a pre-recorded voicemail when a call goes to voicemail.
                          <a href="/settings?tab=subscription" className="block text-xs text-primary font-medium mt-1 hover:underline">
                            Upgrade to Full-Fledged to unlock this feature →
                          </a>
                        </span>
                      ) : (
                        <>
                          Automatically leave a pre-recorded voicemail when a call goes to voicemail — works in both Power Dialer and Multi-Line sessions
                          {!hasDefaultVoicemailDrop && preferences.autoVoicemailEnabled && (
                            <span className="block text-xs text-amber-600 mt-0.5">
                              Upload and set a default voicemail drop below to activate
                            </span>
                          )}
                        </>
                      )}
                    </p>
                  </div>
                </div>
                <Switch
                  id="auto-voicemail"
                  checked={isBasicPlan ? false : preferences.autoVoicemailEnabled}
                  disabled={isBasicPlan}
                  onCheckedChange={(checked) =>
                    setPreferences((prev) => ({ ...prev, autoVoicemailEnabled: checked }))
                  }
                />
              </div>

              {/* Voicemail Drop Library + Source Rules — shown when Auto AI Voice Drop is enabled */}
              <AnimatePresence>
                {preferences.autoVoicemailEnabled && !isBasicPlan && (
                  <motion.div {...expand} className="overflow-hidden">
                    <div className="px-5 pb-5 pt-2 space-y-5">
                      <VoicemailDropLibrary />

                      {/* Per-tag / per-stage / per-source voicemail rules.
                          Resolver precedence at drop time: tag → stage → source → default. */}
                      {voicemailDrops.length > 0 && (
                        <>
                          <VoicemailTagRules
                            voicemailDrops={voicemailDrops}
                            rules={preferences.voicemailDropTagRules || {}}
                            onRulesChange={(rules: Record<string, string>) =>
                              setPreferences((prev) => ({ ...prev, voicemailDropTagRules: rules }))
                            }
                          />
                          <VoicemailStageRules
                            voicemailDrops={voicemailDrops}
                            rules={preferences.voicemailDropStageRules || {}}
                            onRulesChange={(rules: Record<string, string>) =>
                              setPreferences((prev) => ({ ...prev, voicemailDropStageRules: rules }))
                            }
                          />
                          <VoicemailSourceRules
                            voicemailDrops={voicemailDrops}
                            rules={preferences.voicemailDropRules || {}}
                            onRulesChange={(rules: Record<string, string>) =>
                              setPreferences((prev) => ({ ...prev, voicemailDropRules: rules }))
                            }
                          />
                        </>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

          {/* ─── Voice Clone (AI Voicemail) ─── */}
          <motion.div variants={fadeUp}>
            <VoiceCloneSection />
          </motion.div>

          {/* ─── Call Scripts ─── */}
          <motion.div variants={fadeUp}>
            <CallScriptsSection />
          </motion.div>

          {/* ─── DialIQ Smart Redial ─── (hidden in prod until feature is ready) */}
          {DIAL_IQ_ENABLED && (
            <motion.div variants={fadeUp}>
              <DialIQSettings
                initial={(user?.user as any)?.appPreferences?.dialIq}
                onSave={handleDialIqSave}
              />
            </motion.div>
          )}

          {/* ─── Call Recording ─── */}
          <motion.div variants={fadeUp}>
            <div
              className={cn(
                "relative rounded-xl border transition-all duration-300",
                "hover:shadow-md hover:shadow-black/[0.03]",
                preferences.callRecordingEnabled
                  ? "border-primary/20 bg-primary/[0.015]"
                  : "border-border"
              )}
            >
              <div
                className={cn(
                  "absolute left-0 top-3 bottom-3 w-[3px] rounded-full transition-all duration-500",
                  preferences.callRecordingEnabled ? "bg-primary" : "bg-transparent"
                )}
              />

              <div className="flex items-center justify-between p-4 pl-5">
                <div className="flex items-center gap-4">
                  <div
                    className={cn(
                      "flex items-center justify-center h-10 w-10 rounded-lg transition-colors duration-300",
                      preferences.callRecordingEnabled
                        ? "bg-primary/10 text-primary"
                        : "bg-muted text-muted-foreground"
                    )}
                  >
                    <Mic className="h-5 w-5" />
                  </div>
                  <div>
                    <Label
                      htmlFor="call-recording"
                      className="text-[15px] font-semibold leading-none cursor-pointer"
                    >
                      Call Recording
                    </Label>
                    <p className="text-sm text-muted-foreground mt-1.5 max-w-md">
                      Automatically record all calls for quality assurance and training
                    </p>
                  </div>
                </div>
                <Switch
                  id="call-recording"
                  checked={preferences.callRecordingEnabled}
                  onCheckedChange={(checked) =>
                    handleToggleChange("callRecordingEnabled", checked)
                  }
                />
              </div>

              <AnimatePresence>
                {preferences.callRecordingEnabled && (
                  <motion.div {...expand} className="overflow-hidden">
                    <div className="px-5 pb-4 pl-[76px]">
                      <div className="p-3 rounded-lg border-l-2 border-l-destructive/60 bg-destructive/5">
                        <p className="text-xs text-foreground/70 leading-relaxed">
                          <strong>Compliance:</strong> All inbound and outbound calls will be
                          recorded. Please inform callers and obtain their consent.
                        </p>
                      </div>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </motion.div>

        </motion.div>
      </CardContent>
    </Card>
    </>
  );
}

// ── Call Scripts CRUD Section ──────────────────────────────────
function CallScriptsSection() {
  const { toast } = useToast();
  const { user } = useUserContext();
  const agencyRole = user?.user?.agencyRole || user?.currentAgency?.role;
  const isOwnerOrAdmin = agencyRole === 'owner' || agencyRole === 'admin' || agencyRole === 'OWNER' || agencyRole === 'ADMIN';
  const currentUserId = user?.user?.id;

  const { data: scripts = [], isLoading } = useCallScripts();
  const createScript = useCreateScript();
  const updateScript = useUpdateScript();
  const deleteScript = useDeleteScript();
  const duplicateTemplate = useDuplicateTemplate();

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formName, setFormName] = useState("");
  const [formContent, setFormContent] = useState("");
  const [formCategory, setFormCategory] = useState("custom");
  const [formProductType, setFormProductType] = useState("");
  const [formShareWithAgency, setFormShareWithAgency] = useState(false);
  const [formScope, setFormScope] = useState<string>("agency-only");
  const [activeTab, setActiveTab] = useState("all");

  const filteredScripts = useMemo(() => {
    if (activeTab === "all") return scripts;
    return scripts.filter((s) => s.category === activeTab);
  }, [scripts, activeTab]);

  const categoryCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const cat of SCRIPT_CATEGORIES) {
      counts[cat.value] = cat.value === "all" ? scripts.length : scripts.filter((s) => s.category === cat.value).length;
    }
    return counts;
  }, [scripts]);

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormName("");
    setFormContent("");
    setFormCategory("custom");
    setFormProductType("");
    setFormShareWithAgency(false);
    setFormScope("agency-only");
  };

  const startEdit = (script: CallScript) => {
    setEditingId(script.id);
    setFormName(script.name);
    setFormContent(script.content);
    setFormCategory(script.category || "custom");
    setFormProductType(script.productType || "");
    setFormShareWithAgency(!!script.agencyId);
    setFormScope((script as any).scope || "agency-only");
    setShowForm(true);
  };

  const handleSave = async () => {
    if (!formName.trim() || !formContent.trim()) {
      toast({ title: "Missing fields", description: "Name and content are required.", variant: "destructive" });
      return;
    }
    try {
      if (editingId) {
        await updateScript.mutateAsync({
          id: editingId,
          data: {
            name: formName.trim(),
            content: formContent,
            category: formCategory,
            ...(isOwnerOrAdmin ? { shareWithAgency: formShareWithAgency, scope: formShareWithAgency ? formScope : 'personal' } : {}),
          },
        });
        toast({ title: "Script updated" });
      } else {
        await createScript.mutateAsync({
          name: formName.trim(),
          content: formContent,
          category: formCategory,
          productType: formProductType || undefined,
          ...(isOwnerOrAdmin ? { shareWithAgency: formShareWithAgency, scope: formShareWithAgency ? formScope : 'personal' } : {}),
        });
        toast({ title: "Script created" });
      }
      resetForm();
    } catch {
      toast({ title: "Error", description: "Failed to save script.", variant: "destructive" });
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteScript.mutateAsync(id);
      toast({ title: "Script deleted" });
    } catch {
      toast({ title: "Error", description: "Failed to delete script.", variant: "destructive" });
    }
  };

  const handleSetDefault = async (script: CallScript) => {
    try {
      await updateScript.mutateAsync({ id: script.id, data: { isDefault: !script.isDefault } });
    } catch {
      toast({ title: "Error", description: "Failed to update default.", variant: "destructive" });
    }
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith(".txt")) {
      toast({ title: "Invalid file", description: "Only .txt files are supported.", variant: "destructive" });
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const text = ev.target?.result;
      if (typeof text === "string") {
        setFormContent(text);
        if (!formName.trim()) setFormName(file.name.replace(/\.txt$/, ""));
        setShowForm(true);
      }
    };
    reader.readAsText(file);
    e.target.value = "";
  };

  const handleDuplicate = async (id: string) => {
    try {
      await duplicateTemplate.mutateAsync(id);
      toast({ title: "Template duplicated to your scripts" });
    } catch {
      toast({ title: "Error", description: "Failed to duplicate template.", variant: "destructive" });
    }
  };

  return (
    <div className="rounded-xl border hover:shadow-md hover:shadow-black/[0.03] transition-all duration-300">
      {/* Header row */}
      <div className="flex items-center justify-between p-4 pl-5">
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-muted text-muted-foreground">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <Label className="text-[15px] font-semibold leading-none">Call Scripts</Label>
            <p className="text-sm text-muted-foreground mt-1.5">
              Manage scripts to read during calls
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className="cursor-pointer">
            <input type="file" accept=".txt" className="hidden" onChange={handleFileUpload} />
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border bg-background hover:bg-muted/50 transition-colors cursor-pointer">
              <Upload className="h-3.5 w-3.5" />
              Upload
            </span>
          </label>
          <Button
            size="sm"
            variant="outline"
            className="rounded-lg h-8 text-xs"
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
          >
            <Plus className="h-3.5 w-3.5 mr-1" />
            Add Script
          </Button>
        </div>
      </div>

      {/* Form */}
      <AnimatePresence>
        {showForm && (
          <motion.div {...expand} className="overflow-hidden">
            <div className="mx-4 mb-4 p-4 rounded-lg border bg-muted/30 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">
                  {editingId ? "Edit Script" : "New Script"}
                </span>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={resetForm}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
              <div className="space-y-3">
                <div>
                  <Label className="text-xs text-muted-foreground">Name</Label>
                  <Input
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder="Script name"
                    className="mt-1 h-8 text-sm"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs text-muted-foreground">Script Type</Label>
                    <select
                      value={formCategory}
                      onChange={(e) => setFormCategory(e.target.value)}
                      className="mt-1 flex h-8 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      {SCRIPT_CATEGORIES.filter((c) => c.value !== "all").map((c) => (
                        <option key={c.value} value={c.value}>{c.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <Label className="text-xs text-muted-foreground">Product Type</Label>
                    <select
                      value={formProductType}
                      onChange={(e) => setFormProductType(e.target.value)}
                      className="mt-1 flex h-8 w-full rounded-md border border-input bg-background px-3 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                    >
                      <option value="">None</option>
                      {PRODUCT_TYPES.map((p) => (
                        <option key={p.value} value={p.value}>{p.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Script Content</Label>
                <Textarea
                  value={formContent}
                  onChange={(e) => setFormContent(e.target.value)}
                  placeholder="Type or paste your script here..."
                  className="mt-1 min-h-[120px] text-sm"
                />
              </div>
              {isOwnerOrAdmin && (
                <div className="space-y-2">
                  <div className="flex items-center justify-between p-2.5 rounded-lg border bg-muted/20">
                    <div className="flex items-center gap-2">
                      <Building2 className="h-3.5 w-3.5 text-muted-foreground" />
                      <div>
                        <Label className="text-xs font-medium">Share with agency</Label>
                        <p className="text-[11px] text-muted-foreground">Make this script available to your team</p>
                      </div>
                    </div>
                    <Switch
                      checked={formShareWithAgency}
                      onCheckedChange={setFormShareWithAgency}
                    />
                  </div>
                  {formShareWithAgency && (() => {
                    const parentAgencyId = (user as any)?.currentAgency?.agency?.parentAgencyId;
                    const isRootAgency = !parentAgencyId;
                    const scopeOptions = [
                      { value: 'agency-only', label: 'My Agency', desc: 'Direct team only' },
                      { value: 'agency-and-below', label: 'Agency + Subs', desc: 'Flows down to sub-agencies' },
                      ...(isRootAgency ? [{ value: 'agency-wide', label: 'Agency-Wide', desc: 'Entire network' }] : []),
                    ];
                    return (
                    <div className={`grid gap-1.5 p-1.5 rounded-lg border bg-muted/10 ${isRootAgency ? 'grid-cols-3' : 'grid-cols-2'}`}>
                      {scopeOptions.map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={() => setFormScope(opt.value)}
                          className={`p-2 rounded-md text-center transition-all text-xs ${
                            formScope === opt.value
                              ? 'bg-primary text-primary-foreground shadow-sm'
                              : 'hover:bg-muted/50 text-muted-foreground'
                          }`}
                        >
                          <p className="font-semibold">{opt.label}</p>
                          <p className={`text-[9px] mt-0.5 ${formScope === opt.value ? 'text-primary-foreground/70' : 'text-muted-foreground/70'}`}>{opt.desc}</p>
                        </button>
                      ))}
                    </div>
                    );
                  })()}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={resetForm}>
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={handleSave}
                  disabled={createScript.isPending || updateScript.isPending}
                >
                  {(createScript.isPending || updateScript.isPending) && (
                    <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                  )}
                  {editingId ? "Update" : "Create"}
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Category tabs */}
      <div className="px-4 pt-2 pb-1 flex items-center gap-1.5 overflow-x-auto scrollbar-none">
        {SCRIPT_CATEGORIES.map((cat) => (
          <button
            key={cat.value}
            onClick={() => setActiveTab(cat.value)}
            className={cn(
              "text-xs px-2.5 py-1 rounded-full whitespace-nowrap transition-colors flex-shrink-0 flex items-center gap-1.5",
              activeTab === cat.value
                ? "bg-primary text-primary-foreground"
                : "bg-muted/60 text-muted-foreground hover:bg-muted hover:text-foreground"
            )}
          >
            {cat.label}
            {categoryCounts[cat.value] > 0 && (
              <span className={cn(
                "text-[10px] px-1.5 rounded-full",
                activeTab === cat.value ? "bg-primary-foreground/20" : "bg-foreground/10"
              )}>
                {categoryCounts[cat.value]}
              </span>
            )}
          </button>
        ))}
      </div>

      {/* Scripts list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          Loading scripts...
        </div>
      ) : filteredScripts.length === 0 && !showForm ? (
        <div className="py-8 text-center">
          <FileText className="h-8 w-8 text-muted-foreground/30 mx-auto" />
          <p className="text-sm text-muted-foreground mt-2">
            {activeTab === "all" ? "No scripts yet" : `No ${SCRIPT_CATEGORIES.find((c) => c.value === activeTab)?.label || ""} scripts`}
          </p>
          <p className="text-xs text-muted-foreground/60 mt-0.5">
            Add a script or upload a .txt file to get started
          </p>
        </div>
      ) : (
        <div className="px-4 pb-4 space-y-1.5">
          {filteredScripts.map((script) => (
            <div
              key={script.id}
              className="group/row flex items-center justify-between p-3 rounded-lg border border-transparent hover:border-border hover:bg-muted/30 transition-all duration-200"
            >
              <div className="flex items-center gap-3 min-w-0">
                <div className="flex items-center justify-center h-8 w-8 rounded-md bg-primary/5 text-primary/60 flex-shrink-0">
                  <FileText className="h-3.5 w-3.5" />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-medium truncate">{script.name}</span>
                    {script.isDefault && (
                      <Star className="h-3 w-3 fill-amber-400 text-amber-500 flex-shrink-0" />
                    )}
                    {script.isTemplate && (
                      <Badge
                        variant="secondary"
                        className="text-[10px] px-1.5 py-0 h-4 bg-primary/10 text-primary border-0"
                      >
                        Template
                      </Badge>
                    )}
                    {script.agencyId && !script.isTemplate && (
                      <Badge
                        variant="secondary"
                        className="text-[10px] px-1.5 py-0 h-4 bg-blue-500/10 text-blue-600 border-0 flex items-center gap-0.5"
                      >
                        <Building2 className="h-2.5 w-2.5" />
                        {(script as any).scope === 'agency-wide' ? 'Agency-Wide' : (script as any).scope === 'agency-and-below' ? 'Agency + Subs' : 'Agency'}
                      </Badge>
                    )}
                    {script.category && script.category !== "custom" && (
                      <Badge
                        variant="secondary"
                        className="text-[10px] px-1.5 py-0 h-4"
                      >
                        {SCRIPT_CATEGORIES.find((c) => c.value === script.category)?.label || script.category}
                      </Badge>
                    )}
                    {script.productType && (
                      <Badge
                        variant="outline"
                        className="text-[10px] px-1.5 py-0 h-4"
                      >
                        {PRODUCT_TYPES.find((p) => p.value === script.productType)?.label || script.productType}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground truncate max-w-[300px]">
                    {script.content.substring(0, 80)}
                    {script.content.length > 80 ? "..." : ""}
                  </p>
                </div>
              </div>

              <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover/row:opacity-100 transition-opacity duration-200">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0"
                  onClick={() => handleSetDefault(script)}
                  title={script.isDefault ? "Remove as default" : "Set as default"}
                >
                  <Star
                    className={cn(
                      "h-3.5 w-3.5",
                      script.isDefault
                        ? "fill-amber-400 text-amber-500"
                        : "text-muted-foreground/40"
                    )}
                  />
                </Button>
                {script.isTemplate ? (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    onClick={() => handleDuplicate(script.id)}
                    disabled={duplicateTemplate.isPending}
                    title="Duplicate to my scripts"
                  >
                    <Copy className="h-3.5 w-3.5 text-primary/60" />
                  </Button>
                ) : (
                  <>
                    {/* Show edit/delete if user is creator OR agency owner/admin for agency scripts */}
                    {(script.userId === currentUserId || (script.agencyId && isOwnerOrAdmin)) && (
                      <>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0"
                          onClick={() => startEdit(script)}
                          title="Edit"
                        >
                          <Pencil className="h-3.5 w-3.5 text-muted-foreground/60" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-7 w-7 p-0 hover:text-destructive"
                          onClick={() => handleDelete(script.id)}
                          disabled={deleteScript.isPending}
                          title="Delete"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                  </>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Voice Clone Section ──────────────────────────────────────
const VOICE_STATUS: Record<string, { label: string; color: string; icon: React.ReactNode }> = {
  ready: { label: "Ready", color: "bg-green-100 text-green-700", icon: <CheckCircle className="h-3 w-3" /> },
  processing: { label: "Processing", color: "bg-yellow-100 text-yellow-700", icon: <Clock className="h-3 w-3" /> },
  failed: { label: "Failed", color: "bg-red-100 text-red-700", icon: <AlertCircle className="h-3 w-3" /> },
};

function VoiceCloneSection() {
  const { user } = useUserContext();
  // SUPER AGENT ONLY (narrowed 2026-07-29 — Enterprise removed by product
  // decision). Must stay in lockstep with `canUseVoiceCloning` in the backend's
  // user.constants.ts, which is the enforcing check: if this grants access the
  // backend doesn't, the agent records a voice sample and then eats a 403 on
  // upload.
  const isSuperAgent = user?.user?.accountType === "super_agent";
  const { data: voiceClones = [], isLoading } = useVoiceClones();
  const createMutation = useCreateVoiceClone();
  const deleteMutation = useDeleteVoiceClone();
  const setDefaultMutation = useSetDefaultVoiceClone();
  const previewMutation = usePreviewVoiceClone();
  const testRvmMutation = useTestRVM();

  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState("");
  const [consentChecked, setConsentChecked] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewText, setPreviewText] = useState(
    "Hey there, this is a test of my cloned voice. I wanted to reach out and let you know about some exciting options we have for you."
  );
  const [previewAudioUrl, setPreviewAudioUrl] = useState<string | null>(null);
  const [playingAudioUrl, setPlayingAudioUrl] = useState<string | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  // Recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingDuration, setRecordingDuration] = useState(0);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const audioRef = useRef<HTMLAudioElement>(null);

  const MIN_RECORDING_SECONDS = 30;
  const MAX_RECORDING_SECONDS = 180;
  const MAX_FILE_SIZE_MB = 10;

  const cleanupRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    mediaRecorderRef.current = null;
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      cleanupRecording();
      if (objectUrlRef.current) {
        URL.revokeObjectURL(objectUrlRef.current);
        objectUrlRef.current = null;
      }
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current.removeAttribute("src");
      }
    };
  }, [cleanupRecording]);

  useEffect(() => {
    if (isRecording && recordingDuration >= MAX_RECORDING_SECONDS) {
      handleStopRecording();
      showToast("Maximum recording duration reached (3 minutes)", "info");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recordingDuration, isRecording]);

  const getSupportedMimeType = (): { mimeType: string; ext: string } => {
    const options = [
      { mimeType: "audio/webm;codecs=opus", ext: "webm" },
      { mimeType: "audio/webm", ext: "webm" },
      { mimeType: "audio/mp4", ext: "m4a" },
      { mimeType: "audio/ogg;codecs=opus", ext: "ogg" },
    ];
    for (const opt of options) {
      if (typeof MediaRecorder !== "undefined" && MediaRecorder.isTypeSupported(opt.mimeType)) return opt;
    }
    return { mimeType: "", ext: "webm" };
  };

  const handleStartRecording = async () => {
    try {
      if (typeof navigator?.mediaDevices?.getUserMedia !== "function") {
        showToast("Your browser does not support audio recording", "error");
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      const { mimeType, ext } = getSupportedMimeType();
      const recorderOptions = mimeType ? { mimeType } : undefined;
      const mediaRecorder = new MediaRecorder(stream, recorderOptions);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];
      const actualMime = mediaRecorder.mimeType || mimeType || "audio/webm";

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: actualMime });
        setSelectedFile(new File([blob], `voice_sample_${Date.now()}.${ext}`, { type: actualMime }));
        stream.getTracks().forEach((t) => t.stop());
        streamRef.current = null;
      };

      mediaRecorder.start();
      setIsRecording(true);
      setRecordingDuration(0);
      timerRef.current = setInterval(() => setRecordingDuration((d) => d + 1), 1000);
    } catch (err: any) {
      const msg = err?.name === "NotAllowedError"
        ? "Microphone access denied. Please allow microphone access in your browser settings."
        : err?.name === "NotFoundError"
        ? "No microphone found. Please connect a microphone and try again."
        : `Failed to start recording: ${err?.message || "Unknown error"}`;
      showToast(msg, "error");
    }
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") mediaRecorderRef.current.stop();
    setIsRecording(false);
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
  };

  const handleCreate = () => {
    if (!selectedFile || !name.trim()) return;
    if (!consentChecked) {
      showToast("Please confirm you own this voice or have written permission.", "error");
      return;
    }
    if (selectedFile.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      showToast(`File is too large. Maximum size is ${MAX_FILE_SIZE_MB}MB.`, "error");
      return;
    }
    if (recordingDuration > 0 && recordingDuration < MIN_RECORDING_SECONDS) {
      showToast(`Recording is too short. Please record at least ${MIN_RECORDING_SECONDS} seconds.`, "error");
      return;
    }
    createMutation.mutate({ file: selectedFile, name: name.trim(), consentAcknowledged: true }, {
      onSuccess: () => {
        setName(""); setSelectedFile(null); setRecordingDuration(0); setShowForm(false); setConsentChecked(false);
        showToast("Voice clone created successfully!", "success");
      },
      onError: (err: any) => {
        const msg = err?.response?.data?.message || err?.message || "Voice cloning failed. Please check your audio quality and try again.";
        showToast(msg, "error");
      },
    });
  };

  /**
   * Play a clip through the shared <audio> element.
   *
   * This used to be `.catch(() => setPlayingAudioUrl(null))` — every failure was
   * swallowed, so a browser autoplay block, a dead media URL and a decode error
   * all looked identical to the user: the spinner stopped and nothing happened.
   * That silence is what made "Generate Preview doesn't work" undiagnosable from
   * the UI.
   *
   * Autoplay rejection is NOT a failure — the clip is fine, the browser just
   * won't start it without a direct click — so it gets a calm nudge toward the
   * play button rather than a scary error. Everything else is surfaced and
   * logged with the URL so it can actually be chased down.
   *
   * NOTE: `play()` does NOT reliably reject when the *source* fails to load —
   * the element fires an `error` event instead. That's handled by onError on
   * the <audio> element itself; both paths are needed for full coverage.
   *
   * Do NOT add preload="none" or an explicit load() here. Assigning .src
   * already starts resource selection; forcing load() immediately before
   * play() races it and Chrome rejects with NotSupportedError.
   */
  /**
   * Load a remote clip and play it from a blob: URL instead of pointing the
   * <audio> element straight at the remote URL.
   *
   * WHY NOT JUST audio.src = url
   * ----------------------------
   * A media element reports remote failures as an opaque `MediaError` — code 4
   * ("format not supported") is what you get for a 404, a CORS rejection, an
   * extension-blocked request AND a genuinely bad codec. That is unfixable to
   * diagnose from the UI: the audio in question was verified as a valid MPEG-1
   * Layer III file, served with HTTP 206 + audio/mpeg, and confirmed playable
   * (`canPlayType: "probably"`) in a clean browser — yet the element still
   * refused it in one specific browser profile.
   *
   * Fetching first turns that into an ordinary HTTP result we can read and
   * report: a status code, a CORS error, or a real byte count and MIME type.
   * Playback then happens from a blob, which is same-origin, immune to
   * extension/URL-level blocking, and already permitted by our CSP (media-src
   * includes blob:).
   */
  const objectUrlRef = useRef<string | null>(null);

  const loadAndPlay = async (url: string) => {
    const el = audioRef.current;
    if (!el) return;
    try {
      const res = await fetch(url, { mode: "cors", cache: "no-store" });
      if (!res.ok) {
        // NOTE: interpolate into the string — Next's error overlay renders a
        // second console.error argument as `{}`, which hid the status entirely.
        // eslint-disable-next-line no-console
        console.error(
          `[voice-preview] fetch failed: HTTP ${res.status} "${res.statusText}" type=${res.type} url=${url}`,
        );
        showToast(
          `Could not download the audio (HTTP ${res.status}${res.statusText ? " " + res.statusText : ""}). Tried: ${url}`,
          "error",
        );
        return;
      }
      const blob = await res.blob();
      if (!blob.size) {
        showToast("The audio file came back empty.", "error");
        return;
      }
      // eslint-disable-next-line no-console
      console.info(
        `[voice-preview] fetched OK: ${blob.size} bytes, type="${blob.type}" url=${url}`,
      );
      if (objectUrlRef.current) URL.revokeObjectURL(objectUrlRef.current);
      objectUrlRef.current = URL.createObjectURL(blob);
      el.src = objectUrlRef.current;
      safePlay(el, url);
    } catch (err: unknown) {
      // A network-level throw here (as opposed to a status code) is almost
      // always CORS or a blocking extension — neither of which the media
      // element would have told us about.
      // eslint-disable-next-line no-console
      console.error(
        `[voice-preview] fetch threw: ${(err as { name?: string })?.name || ""} ${(err as { message?: string })?.message || ""} url=${url}`,
      );
      showToast(
        `Could not reach the audio file (${(err as { message?: string })?.message || "network error"}). If you use an ad blocker or privacy extension, try an incognito window.`,
        "error",
      );
    }
  };

  const safePlay = (audio: HTMLAudioElement, url: string) => {
    audio
      .play()
      .then(() => setPlayingAudioUrl(url))
      .catch((err: unknown) => {
        setPlayingAudioUrl(null);
        const name = (err as { name?: string })?.name;
        if (name === "NotAllowedError") {
          showToast("Preview is ready — press play to listen.", "info");
          return;
        }
        // AbortError just means a newer src replaced this one (fast re-click).
        // Benign — the newer play() owns the UI state from here.
        if (name === "AbortError") return;
        // eslint-disable-next-line no-console
        console.error("[voice-preview] playback failed", {
          url,
          name,
          message: (err as { message?: string })?.message,
        });
        showToast(
          `Couldn't play the audio (${name || "unknown error"}). It was generated successfully — try the play button.`,
          "error",
        );
      });
  };

  /**
   * Media-level failures (404, CORS, unsupported codec) surface HERE, not as a
   * rejected play() promise. Without this the element failed completely
   * silently.
   */
  const handleAudioError = () => {
    const el = audioRef.current;
    const code = el?.error?.code;
    const reason =
      code === 1 ? "load aborted"
      : code === 2 ? "network error"
      : code === 3 ? "decode error"
      : code === 4 ? "audio format not supported"
      : "unknown error";
    setPlayingAudioUrl(null);
    const src = el?.currentSrc || el?.src || "(no src)";
    // eslint-disable-next-line no-console
    console.error("[voice-preview] media error", {
      src,
      code,
      reason,
      canPlayMp3: el?.canPlayType?.("audio/mpeg") || "(empty = MP3 unsupported)",
      message: el?.error?.message,
    });
    // The src is surfaced in the toast on purpose: every server-side layer has
    // been verified good (valid MP3, HTTP 206 on Range, correct MIME, playable
    // in a clean browser), so the only thing that explains a failure here is the
    // element pointing somewhere unexpected. Showing it turns a dead end into a
    // one-glance diagnosis.
    showToast(
      `Could not load the audio (${reason}). Tried: ${src.slice(0, 120)}`,
      "error",
    );
  };

  const handlePlayAudio = (url: string) => {
    if (!audioRef.current) return;
    if (playingAudioUrl === url) { audioRef.current.pause(); setPlayingAudioUrl(null); }
    else { loadAndPlay(url); }
  };

  const handlePreview = (clone: VoiceClone) => {
    previewMutation.mutate({ id: clone.id, text: previewText.trim() }, {
      onSuccess: (data) => {
        setPreviewAudioUrl(data.audioUrl);
        if (audioRef.current) { loadAndPlay(data.audioUrl); }
      },
      onError: (err: any) => {
        const msg = err?.response?.data?.message || err?.message || "Failed to generate preview. Please try again.";
        showToast(msg, "error");
      },
    });
  };

  const formatDuration = (secs: number) => {
    const m = Math.floor(secs / 60);
    const s = secs % 60;
    return `${m}:${s.toString().padStart(2, "0")}`;
  };

  const hasReadyClones = voiceClones.some((c) => c.status === "ready");

  // Teaser for non-super-agent users
  if (!isSuperAgent) {
    return (
      <div className="rounded-xl border relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-amber-500/5 via-transparent to-amber-500/5 pointer-events-none" />
        <div className="flex items-center justify-between p-4 pl-5">
          <div className="flex items-center gap-4">
            <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-amber-100 text-amber-600">
              <Mic className="h-5 w-5" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <Label className="text-[15px] font-semibold leading-none">Insura AI Voice Clone</Label>
                <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 text-[10px] px-1.5 py-0">
                  <InsuraBotIcon className="h-3 w-3 mr-0.5" />
                  Super Agent
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground mt-1.5 max-w-md">
                Clone your voice with AI to send personalized voicemails without ringing the lead&apos;s phone
              </p>
            </div>
          </div>
          <Button
            size="sm"
            disabled
            className="rounded-lg h-8 text-xs bg-amber-600 text-white cursor-default pointer-events-none disabled:opacity-100"
          >
            <Clock className="h-3 w-3 mr-1" />
            Coming Soon
          </Button>
        </div>
        <div className="px-5 pb-4 pl-[76px]">
          <div className="grid grid-cols-3 gap-3">
            {[
              { title: "AI Voice Cloning", desc: "Clone your voice from a 30-second recording" },
              { title: "Ringless Voicemail", desc: "Drop voicemails without ringing their phone" },
              { title: "Auto Sentimentals", desc: "Birthday & holiday voicemails on autopilot" },
            ].map((feature) => (
              <div key={feature.title} className="p-2.5 rounded-lg border border-dashed border-amber-200 bg-amber-50/50">
                <p className="text-xs font-medium text-amber-900">{feature.title}</p>
                <p className="text-[10px] text-amber-600 mt-0.5">{feature.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="rounded-xl border hover:shadow-md hover:shadow-black/[0.03] transition-all duration-300">
      <audio
        ref={audioRef}
        onEnded={() => setPlayingAudioUrl(null)}
        onError={handleAudioError}
      />

      {/* Header */}
      <div className="flex items-center justify-between p-4 pl-5">
        <div className="flex items-center gap-4">
          <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-muted text-muted-foreground">
            <Mic className="h-5 w-5" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <Label className="text-[15px] font-semibold leading-none">Insura AI Voice Clone</Label>
              {hasReadyClones && (
                <Badge variant="outline" className="bg-emerald-50 text-emerald-700 border-emerald-200 text-[10px] px-1.5 py-0">
                  Voicemail Drops Active
                </Badge>
              )}
            </div>
            <p className="text-sm text-muted-foreground mt-1.5 max-w-md">
              Clone your voice with AI to send personalized voicemails without ringing the lead&apos;s phone
              {hasReadyClones && (
                <span className="block text-xs text-emerald-600 mt-0.5">
                  Sentimentals will automatically include a voicemail drop alongside SMS
                </span>
              )}
            </p>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          className="rounded-lg h-8 text-xs"
          onClick={() => setShowForm(!showForm)}
        >
          {showForm ? <><X className="h-3.5 w-3.5 mr-1" />Cancel</> : <><Plus className="h-3.5 w-3.5 mr-1" />New Clone</>}
        </Button>
      </div>

      {/* How it differs from Auto Voicemail */}
      <div className="px-5 pb-3 pl-[76px]">
        <div className="p-3 rounded-lg border-l-2 border-l-primary bg-primary/5">
          <p className="text-xs text-foreground/70 leading-relaxed">
            <strong>How is this different from Auto Voicemail?</strong> Auto Voicemail plays the same pre-recorded
            audio when a lead doesn&apos;t answer during a dialer session. Voice Clone uses AI to generate a
            <strong> personalized message in your own voice</strong> and delivers it directly to the lead&apos;s
            voicemail box <strong>without their phone ever ringing</strong> — perfect for birthdays, policy
            renewals, and re-engagement campaigns.
          </p>
        </div>
      </div>

      {/* Create form */}
      <AnimatePresence>
        {showForm && (
          <motion.div {...expand} className="overflow-hidden">
            <div className="mx-4 mb-4 p-4 rounded-lg border bg-muted/30 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm font-semibold">New Voice Clone</span>
                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setShowForm(false)}>
                  <X className="h-3.5 w-3.5" />
                </Button>
              </div>
              <div>
                <Label className="text-xs text-muted-foreground">Voice Name</Label>
                <Input
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="My Sales Voice"
                  maxLength={100}
                  className="mt-1 h-8 text-sm"
                />
              </div>
              <div className="flex gap-2">
                {isRecording ? (
                  <Button onClick={handleStopRecording} variant="destructive" size="sm" className="flex-1 rounded-lg h-8 text-xs">
                    <Square className="h-3.5 w-3.5 mr-1.5" />
                    Stop ({formatDuration(recordingDuration)})
                  </Button>
                ) : (
                  <Button onClick={handleStartRecording} variant="outline" size="sm" className="flex-1 rounded-lg h-8 text-xs">
                    <Mic className="h-3.5 w-3.5 mr-1.5" />
                    Record
                  </Button>
                )}
                <Button onClick={() => fileInputRef.current?.click()} variant="outline" size="sm" className="flex-1 rounded-lg h-8 text-xs">
                  <Upload className="h-3.5 w-3.5 mr-1.5" />
                  Upload File
                </Button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="audio/mpeg,audio/wav,audio/mp4,audio/x-m4a,audio/webm,audio/ogg"
                  className="hidden"
                  onChange={(e) => {
                    const file = e.target.files?.[0];
                    if (file) {
                      if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) showToast(`File too large. Max ${MAX_FILE_SIZE_MB}MB.`, "error");
                      else { setSelectedFile(file); setRecordingDuration(0); }
                    }
                    e.target.value = "";
                  }}
                />
              </div>
              {selectedFile && (
                <div className="flex items-center justify-between p-2.5 rounded-lg border bg-background">
                  <span className="text-xs truncate">{selectedFile.name}</span>
                  <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => setSelectedFile(null)}>
                    <Trash2 className="h-3 w-3" />
                  </Button>
                </div>
              )}
              <p className="text-xs text-muted-foreground">Record or upload at least 30 seconds of clear speech. Speak naturally as you would on a call with a client.</p>
              <details className="group">
                <summary className="text-xs text-primary cursor-pointer hover:underline select-none">
                  Need help? Try reading this sample script
                </summary>
                <div className="mt-2 p-3 rounded-lg border bg-background text-xs text-foreground/80 leading-relaxed space-y-2">
                  <p className="font-medium text-foreground">Read this naturally, as if you&apos;re speaking to a client:</p>
                  <blockquote className="border-l-2 border-primary/30 pl-3 italic">
                    &quot;Hi, my name is [your name] and I&apos;m a licensed life insurance agent. I help families
                    protect their future by finding the right coverage at the best price. Whether you&apos;re
                    looking for term, whole life, or final expense insurance, I&apos;m here to guide you through
                    every step of the process. I believe everyone deserves peace of mind knowing their loved
                    ones are taken care of. If you have any questions about your coverage or want to explore
                    your options, feel free to reach out anytime — I&apos;d love to help you and your family.&quot;
                  </blockquote>
                  <p className="text-muted-foreground">
                    <strong>Tips:</strong> Use a quiet room, speak at your normal pace, and keep a consistent tone.
                    Don&apos;t worry about being perfect — natural speech clones best.
                  </p>
                </div>
              </details>
              <label className="flex items-start gap-2 text-xs text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={consentChecked}
                  onChange={(e) => setConsentChecked(e.target.checked)}
                  className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-gray-300 text-primary focus:ring-primary"
                />
                <span>
                  I confirm this voice is <strong>my own</strong> or I have <strong>written permission</strong> from the speaker to clone it. (Required by law — FCC &amp; CA AB&nbsp;1248.)
                </span>
              </label>
              <div className="flex justify-end gap-2">
                <Button variant="ghost" size="sm" onClick={() => setShowForm(false)}>Cancel</Button>
                <Button size="sm" onClick={handleCreate} disabled={!selectedFile || !name.trim() || !consentChecked || createMutation.isPending}>
                  {createMutation.isPending && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
                  Create Clone
                </Button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Clone list */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin mr-2" />
          Loading voice clones...
        </div>
      ) : voiceClones.length === 0 && !showForm ? (
        <div className="py-8 text-center">
          <Volume2 className="h-8 w-8 text-muted-foreground/30 mx-auto" />
          <p className="text-sm text-muted-foreground mt-2">No voice clones yet</p>
          <p className="text-xs text-muted-foreground/60 mt-0.5">
            Click &quot;New Clone&quot; to record or upload a voice sample
          </p>
        </div>
      ) : (
        <div className="px-4 pb-4 space-y-1.5">
          {voiceClones.map((clone) => {
            const statusCfg = VOICE_STATUS[clone.status] || VOICE_STATUS.failed;
            return (
              <div
                key={clone.id}
                className="group/row flex items-center justify-between p-3 rounded-lg border border-transparent hover:border-border hover:bg-muted/30 transition-all duration-200"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <div className="flex items-center justify-center h-8 w-8 rounded-md bg-primary/5 text-primary/60 flex-shrink-0">
                    <Mic className="h-3.5 w-3.5" />
                  </div>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{clone.name}</span>
                      {clone.isDefault && (
                        <Star className="h-3 w-3 fill-amber-400 text-amber-500 flex-shrink-0" />
                      )}
                      <Badge className={`text-[10px] px-1.5 py-0 h-4 border-0 ${statusCfg.color}`}>
                        {statusCfg.icon}
                        <span className="ml-0.5">{statusCfg.label}</span>
                      </Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Created {new Date(clone.createdAt).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-0.5 flex-shrink-0 opacity-0 group-hover/row:opacity-100 transition-opacity duration-200">
                  {clone.sampleAudioUrl && (
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handlePlayAudio(clone.sampleAudioUrl!)} title="Play sample">
                      {playingAudioUrl === clone.sampleAudioUrl ? <Square className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                    </Button>
                  )}
                  {!clone.isDefault && clone.status === "ready" && (
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => setDefaultMutation.mutate(clone.id)} disabled={setDefaultMutation.isPending} title="Set as default">
                      <Star className="h-3.5 w-3.5 text-muted-foreground/40" />
                    </Button>
                  )}
                  {deleteConfirmId === clone.id ? (
                    <Button
                      variant="ghost" size="sm"
                      className="h-auto px-1.5 py-0.5 text-[10px] text-red-600 hover:text-red-800 font-medium"
                      onClick={() => {
                        deleteMutation.mutate(clone.id, {
                          onSuccess: () => { showToast("Voice clone deleted", "success"); setDeleteConfirmId(null); },
                          onSettled: () => setDeleteConfirmId(null),
                        });
                      }}
                      disabled={deleteMutation.isPending}
                    >
                      Confirm
                    </Button>
                  ) : (
                    <Button variant="ghost" size="sm" className="h-7 w-7 p-0 hover:text-destructive" onClick={() => setDeleteConfirmId(clone.id)} disabled={deleteMutation.isPending} title="Delete">
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Preview & Test */}
      {hasReadyClones && (
        <div className="px-4 pb-4">
          <div className="p-4 rounded-lg border bg-muted/30 space-y-3">
            <span className="text-sm font-semibold">Preview & Test</span>
            <div>
              <Label className="text-xs text-muted-foreground">Message Text</Label>
              <Textarea
                value={previewText}
                onChange={(e) => setPreviewText(e.target.value)}
                rows={3}
                maxLength={500}
                className="mt-1 text-sm"
                placeholder="Type the message you want to hear in your cloned voice..."
              />
              <div className="text-xs text-muted-foreground mt-1 text-right">{previewText.length}/500</div>
            </div>
            <div className="flex gap-2">
              <Button
                onClick={() => {
                  const c = voiceClones.find((v) => v.isDefault && v.status === "ready") || voiceClones.find((v) => v.status === "ready");
                  if (c) handlePreview(c);
                }}
                disabled={previewMutation.isPending || !previewText.trim()}
                size="sm" variant="outline" className="flex-1 rounded-lg h-8 text-xs"
              >
                {previewMutation.isPending ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Generating...</> : <><Volume2 className="h-3.5 w-3.5 mr-1.5" />Generate Preview</>}
              </Button>
              <Button
                onClick={() => {
                  const c = voiceClones.find((v) => v.isDefault && v.status === "ready") || voiceClones.find((v) => v.status === "ready");
                  if (c) testRvmMutation.mutate({ id: c.id, text: previewText.trim() }, {
                    onSuccess: () => showToast("Test voicemail sent! Check your phone shortly.", "success"),
                    onError: (err: any) => {
                      const msg = err?.response?.data?.message || err?.message || "Failed to send test voicemail. Please try again.";
                      showToast(msg, "error");
                    },
                  });
                }}
                disabled={testRvmMutation.isPending}
                size="sm" variant="outline" className="flex-1 rounded-lg h-8 text-xs"
              >
                {testRvmMutation.isPending ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />Sending...</> : <><Phone className="h-3.5 w-3.5 mr-1.5" />Test Voicemail to My Phone</>}
              </Button>
            </div>
            {previewAudioUrl && (
              <div className="flex items-center gap-2 p-2.5 rounded-lg border-l-2 border-l-emerald-400 bg-emerald-50/60">
                <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => handlePlayAudio(previewAudioUrl)}>
                  {playingAudioUrl === previewAudioUrl ? <Square className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
                </Button>
                <span className="text-xs text-emerald-800">Preview audio ready — click to play</span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Generic Per-Key Voicemail Rule Mapper (tags, pipeline stages) ─
// Renders one "<key> → voicemail drop" row per key. Shared by the tag- and
// stage-targeting sections. Returns null (renders nothing) when there are no
// keys or no drops, so an agent who hasn't tagged anything sees no clutter.
function VoicemailRuleMapper({
  title,
  description,
  keys,
  isLoading,
  voicemailDrops,
  rules,
  onRulesChange,
}: {
  title: string;
  description: string;
  keys: string[];
  isLoading: boolean;
  voicemailDrops: VoicemailDrop[];
  rules: Record<string, string>;
  onRulesChange: (rules: Record<string, string>) => void;
}) {
  if (isLoading || keys.length === 0 || voicemailDrops.length === 0) return null;

  return (
    <div className="border-t border-gray-100 pt-4">
      <h4 className="text-sm font-semibold text-gray-900 mb-1">{title}</h4>
      <p className="text-xs text-muted-foreground mb-3">{description}</p>
      <div className="space-y-2 max-h-[240px] overflow-y-auto pr-1">
        {keys.map((key) => (
          <div key={key} className="flex items-center gap-3">
            <span className="text-xs font-medium text-gray-700 w-32 truncate" title={key}>
              {key}
            </span>
            <select
              value={rules[key] || ""}
              onChange={(e) => {
                const newRules = { ...rules };
                if (e.target.value) {
                  newRules[key] = e.target.value;
                } else {
                  delete newRules[key];
                }
                onRulesChange(newRules);
              }}
              className="flex-1 h-8 rounded-md border border-input bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="">Use default</option>
              {voicemailDrops.map((drop: VoicemailDrop) => (
                <option key={drop.id} value={drop.id}>
                  {drop.name}{drop.isDefault ? " (default)" : ""}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Per-Tag Voicemail Rules ─────────────────────────────────────
function VoicemailTagRules(props: {
  voicemailDrops: VoicemailDrop[];
  rules: Record<string, string>;
  onRulesChange: (rules: Record<string, string>) => void;
}) {
  const [tags, setTags] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const response = await api.get<any>({ url: getApiUrl("/leads/tags"), auth: true });
        const data = response?.data?.data || response?.data || [];
        setTags(Array.isArray(data) ? data.filter((t: unknown): t is string => typeof t === "string" && !!t) : []);
      } catch {}
      setIsLoading(false);
    })();
  }, []);

  return (
    <VoicemailRuleMapper
      title="Voicemail by Tag"
      description="Leave a different voicemail based on the lead's tags. Checked first — before pipeline stage and source. If a lead has several matching tags, the first one wins."
      keys={tags}
      isLoading={isLoading}
      {...props}
    />
  );
}

// ── Per-Pipeline-Stage Voicemail Rules ──────────────────────────
function VoicemailStageRules(props: {
  voicemailDrops: VoicemailDrop[];
  rules: Record<string, string>;
  onRulesChange: (rules: Record<string, string>) => void;
}) {
  const [stages, setStages] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const response = await api.get<any>({ url: getApiUrl("/leads/pipeline-stages"), auth: true });
        const data = response?.data?.data || response?.data || [];
        const names = Array.isArray(data)
          ? data
              .map((s: any) => (typeof s === "string" ? s : s?.name))
              .filter((n: unknown): n is string => typeof n === "string" && !!n)
              // A "Do not call" lead is never dialed, so a voicemail-drop rule
              // for it makes no sense — hide that stage from the options.
              .filter((n: string) => !/^(do\s*not\s*call|dnc)$/i.test(n.trim()))
          : [];
        setStages(names);
      } catch {}
      setIsLoading(false);
    })();
  }, []);

  return (
    <VoicemailRuleMapper
      title="Voicemail by Pipeline Stage"
      description="Leave a different voicemail based on the lead's current pipeline stage. Checked after tags, before source."
      keys={stages}
      isLoading={isLoading}
      {...props}
    />
  );
}

// ── Per-Source Voicemail Rules ──────────────────────────────────
function VoicemailSourceRules({
  voicemailDrops,
  rules,
  onRulesChange,
}: {
  voicemailDrops: VoicemailDrop[];
  rules: Record<string, string>;
  onRulesChange: (rules: Record<string, string>) => void;
}) {
  const [listNames, setListNames] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const response = await api.get<any>({
          url: getApiUrl("/leads/list-names"),
          auth: true,
        });
        const names = response?.data?.data || response?.data || [];
        setListNames(Array.isArray(names) ? names : []);
      } catch {}
      setIsLoading(false);
    })();
  }, []);

  if (isLoading || listNames.length === 0) return null;

  if (voicemailDrops.length === 0) return null;

  return (
    <div className="border-t border-gray-100 pt-4">
      <h4 className="text-sm font-semibold text-gray-900 mb-1">Voicemail by Lead Source</h4>
      <p className="text-xs text-muted-foreground mb-3">
        Use different voicemails for different lead sources. If no source is set, the default voicemail drop is used.
      </p>
      <div className="space-y-2 max-h-[240px] overflow-y-auto pr-1">
        {listNames.map((source) => (
          <div key={source} className="flex items-center gap-3">
            <span className="text-xs font-medium text-gray-700 w-32 truncate" title={source}>
              {source}
            </span>
            <select
              value={rules[source] || ""}
              onChange={(e) => {
                const newRules = { ...rules };
                if (e.target.value) {
                  newRules[source] = e.target.value;
                } else {
                  delete newRules[source];
                }
                onRulesChange(newRules);
              }}
              className="flex-1 h-8 rounded-md border border-input bg-background px-2 text-xs focus:outline-none focus:ring-1 focus:ring-primary"
            >
              <option value="">Use default</option>
              {voicemailDrops.map((drop: VoicemailDrop) => (
                <option key={drop.id} value={drop.id}>
                  {drop.name}{drop.isDefault ? " (default)" : ""}
                </option>
              ))}
            </select>
          </div>
        ))}
      </div>
    </div>
  );
}
